// Production runs (ERP-PLAN §5.13): one batch of a project, PR-001…, shared by the screens, the server and the MCP.
// Statuses and what can follow, the cost categories and the layer each one lands in, timeline kinds, attachments,
// the opening position of a run recorded after it started, and the mutations' field schemas. The cost math (planned
// and actual stacks, variance, tooling payoff) is shared/costs.ts.

import { z } from "zod";
import { PAID_VIA } from "./pay-accounts.ts";

// Statuses ------------------------------------------------------------------------------------------------------

export const RUN_STATUSES = ["planning", "ordering", "in_production", "shipping", "received", "closed"] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
export const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  planning: "Planning",
  ordering: "Ordering",
  in_production: "In production",
  shipping: "Shipping",
  received: "Received",
  closed: "Closed",
};
export const RUN_STATUS_HINTS: Record<RunStatus, string> = {
  planning: "Units, BOM and dates are being decided; costs use today's prices",
  ordering: "Parts and the build are being ordered; the plan's prices are fixed",
  in_production: "The CM is building",
  shipping: "Finished goods are on their way to HHL",
  received: "Everything has arrived; costs may still come in",
  closed: "Done: its actual cost counts as the item's landed cost",
};
/** Forward one step, or back one to undo a mis-click; closed reopens to received. */
export const NEXT_RUN_STATUSES: Record<RunStatus, readonly RunStatus[]> = {
  planning: ["ordering"],
  ordering: ["in_production", "planning"],
  in_production: ["shipping", "ordering"],
  shipping: ["received", "in_production"],
  received: ["closed", "shipping"],
  closed: ["received"],
};
export const canMoveRun = (from: RunStatus, to: RunStatus) => NEXT_RUN_STATUSES[from].includes(to);
/** The plan (units per variant, BOM revision) changes only while planning or ordering. */
export const planEditable = (s: RunStatus) => s === "planning" || s === "ordering";

// Past runs (migration 057; Mitch, 2026-11-02: "it can be marked as a historical run for product that was already
// completed") --- recorded after the fact: closed from the start, its plan lines are the units it built, and nothing it
// records moves stock or reaches Shopify, vendors or planning.

/** A past run's plan lines (the units it built) change any time; any other run's only while planning or ordering. */
export const runPlanEditable = (run: { status: RunStatus; historical: boolean }) => run.historical || planEditable(run.status);

/** "Ran Mar 3 – Apr 20, 2026" for a past run (its dates on paper). */
export function pastRunText(run: { started_on: string | null; finished_on: string | null }): string {
  const fmt = (d: string, year: boolean) =>
    new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}), timeZone: "UTC" });
  if (!run.finished_on) return "A past run";
  if (!run.started_on) return `Finished ${fmt(run.finished_on, true)}`;
  const sameYear = run.started_on.slice(0, 4) === run.finished_on.slice(0, 4);
  return `Ran ${fmt(run.started_on, !sameYear)} – ${fmt(run.finished_on, true)}`;
}

// Who supplies a part, per run (migration 057; Mitch: "I just don't think it should be static, this would probably be
// per-run") --- the BOM line's supply is the usual way; a run can say otherwise for a part, until the CM reports units
// built against a build that uses it.

export const RUN_SUPPLIES = ["hhl_consigns", "cm_buys", "cm_makes"] as const;
export type RunSupply = (typeof RUN_SUPPLIES)[number];
/** Who supplies it, in words, with the run's CM named ("Elecrow buys it"). */
export const supplyWords = (s: RunSupply, cm: string) =>
  s === "hhl_consigns" ? "HHL sends it" : s === "cm_buys" ? `${cm || "The CM"} buys it` : `${cm || "The CM"} makes it`;

// Costs ---------------------------------------------------------------------------------------------------------

/** The layers of the cost stack, in waterfall order (shared/costs.ts). */
export const LAYERS = ["materials", "assembly", "packaging", "freight", "tooling", "other"] as const;
export type Layer = (typeof LAYERS)[number];
export const LAYER_LABELS: Record<Layer, string> = {
  materials: "Materials",
  assembly: "Assembly & labour",
  packaging: "Packaging & packing",
  freight: "Freight & tariffs",
  tooling: "Tooling & one-time",
  other: "Other",
};
export const LAYER_SHORT: Record<Layer, string> = {
  materials: "Materials",
  assembly: "Assembly",
  packaging: "Packaging",
  freight: "Freight & tariffs",
  tooling: "Tooling",
  other: "Other",
};
export const LAYER_HINTS: Record<Layer, string> = {
  materials: "BOM parts × quantity (scrap allowance included)",
  assembly: "The CM's build fee, PCBA and test services, HHL labour",
  packaging: "Box, sleeve, manual and the packing fee",
  freight: "Freight in to the CM and out to HHL, duty and tariffs, broker and payment fees",
  tooling: "Molds, fixtures, certification and samples, spread over the units they're paid off over",
  other: "Costs that fit nowhere else",
};

export const COST_CATEGORIES = [
  "materials",
  "assembly_labour",
  "packing",
  "mold_tooling",
  "fixture",
  "certification",
  "samples",
  "freight",
  "tariff_duty",
  "broker",
  "payment_fee",
  "other",
  "unknown",
  "backer_shipping",
] as const;
export type CostCategory = (typeof COST_CATEGORIES)[number];
export const COST_CATEGORY_LABELS: Record<CostCategory, string> = {
  materials: "Materials",
  assembly_labour: "Assembly / labour",
  packing: "Packing",
  mold_tooling: "Mold / tooling",
  fixture: "Fixture",
  certification: "Certification",
  samples: "Samples",
  freight: "Freight",
  tariff_duty: "Tariff / duty",
  broker: "Broker",
  payment_fee: "Payment fee",
  other: "Other",
  unknown: "Not known yet",
  backer_shipping: "Shipping to backers",
};
/** Where a cost entry lands in the stack. */
export const CATEGORY_LAYER: Record<CostCategory, Layer> = {
  materials: "materials",
  assembly_labour: "assembly",
  packing: "packaging",
  mold_tooling: "tooling",
  fixture: "tooling",
  certification: "tooling",
  samples: "tooling",
  freight: "freight",
  tariff_duty: "freight",
  broker: "freight",
  payment_fee: "freight",
  other: "other",
  // Paid before its invoice came (a deposit to the CM): in Other until it's known, and on the run's "needs attention".
  unknown: "other",
  // Pre-sales --- never in a unit's cost: runResult() keeps it out of the stack (the run's spend and the pre-sale's result).
  backer_shipping: "other",
};
/** Duty and payment fees follow value (a tariff is a % of it); everything else is split by units. */
export const BY_VALUE: ReadonlySet<CostCategory> = new Set(["tariff_duty", "payment_fee"]);
/** One-time categories: these belong in the tooling ledger when they're spread over more than this run. */
export const ONE_TIME_CATEGORIES: ReadonlySet<CostCategory> = new Set(["mold_tooling", "fixture", "certification", "samples"]);

export const COST_BASES = ["one_time", "per_unit"] as const;
export const COST_BASIS_LABELS = { one_time: "One-time (a total for this run)", per_unit: "Per unit built" } as const;

export const PAID_METHODS = ["wise", "card", "wire", "other"] as const;
export type PaidMethod = (typeof PAID_METHODS)[number];
export const PAID_METHOD_LABELS: Record<PaidMethod, string> = { wise: "Wise", card: "Card", wire: "Wire", other: "Other" };

export const TOOLING_KINDS = ["mold", "fixture", "certification", "sample", "other"] as const;
export type ToolingKind = (typeof TOOLING_KINDS)[number];
export const TOOLING_KIND_LABELS: Record<ToolingKind, string> = {
  mold: "Mold",
  fixture: "Fixture",
  certification: "Certification",
  sample: "Samples",
  other: "Other",
};

// Timeline ------------------------------------------------------------------------------------------------------

export const EVENT_KINDS = [
  "status",
  "po_linked",
  "po_unlinked",
  "po_sent",
  "po_status",
  "po_received",
  "cm_output",
  "cm_scrap",
  "opening",
  "note",
  "photo",
  "qc_report",
  "golden_sample",
  "message",
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];
/** The ones people add; the rest are written as things happen. */
export const MANUAL_EVENT_KINDS = ["note", "photo", "qc_report", "golden_sample", "message"] as const;
export type ManualEventKind = (typeof MANUAL_EVENT_KINDS)[number];
export const EVENT_KIND_LABELS: Record<EventKind, string> = {
  status: "Status",
  po_linked: "PO linked",
  po_unlinked: "PO unlinked",
  po_sent: "PO sent",
  po_status: "PO",
  po_received: "Received",
  cm_output: "CM output",
  cm_scrap: "Scrap",
  opening: "Opening balance",
  note: "Note",
  photo: "Photos",
  qc_report: "QC report",
  golden_sample: "Golden sample",
  message: "Message",
};
export const isManualEvent = (kind: string) => (MANUAL_EVENT_KINDS as readonly string[]).includes(kind);

// Attachments ---------------------------------------------------------------------------------------------------

// Bills and payments --- a bill's invoice, a payment's receipt, and a receipt dropped on a Wise history transfer before it's linked
// Pre-sales --- an income entry's payout statement
export const ATTACHMENT_OWNERS = [
  "production_run",
  "production_cost",
  "production_event",
  "tooling_item",
  "po",
  "bill",
  "payment",
  "wise_transfer",
  "presale_income",
  "landed_cost", // a PO's landed cost: the forwarder's invoice (migration 064)
] as const;
export type AttachmentOwner = (typeof ATTACHMENT_OWNERS)[number];
/** `email`: the email a cost came by, saved as a PDF or a screenshot (migration 076): a cost's document, as an invoice is. */
export const ATTACHMENT_KINDS = ["invoice", "receipt", "quote", "photo", "report", "email", "other"] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];
export const ATTACHMENT_KIND_LABELS: Record<AttachmentKind, string> = {
  invoice: "Invoice",
  receipt: "Payment receipt",
  quote: "Quote",
  photo: "Photo",
  report: "Report",
  email: "Email",
  other: "File",
};
/**
 * Attachments can be up to 100 MB. Production's Caddy lets 30 MB through outside /api/spec-files/* until the
 * /api/attachments/* rule in deploy/Caddyfile.snippet is added; a refused upload says so (ATTACHMENT_PROXY_LIMIT).
 */
export const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024;
export const ATTACHMENT_PROXY_LIMIT_MB = 30;
/** Shown in the browser (images, PDFs, text); anything else downloads. */
export const viewableType = (contentType: string) => /^(application\/pdf|image\/(png|jpeg|webp|gif)|text\/plain)$/.test(contentType);
/** The kind an upload probably is, from its name and type. */
export function guessAttachmentKind(filename: string, contentType: string): AttachmentKind {
  const n = filename.toLowerCase();
  if (/receipt|payment|wise|transfer|remittance/.test(n)) return "receipt";
  if (/invoice|\binv\b|\bpi\b|proforma|bill/.test(n)) return "invoice";
  if (/quot/.test(n)) return "quote";
  if (/qc|inspection|report|test/.test(n)) return "report";
  if (contentType.startsWith("image/")) return "photo";
  return "other";
}

// Opening position ------------------------------------------------------------------------------------------------

/**
 * What had already happened on a run before it was recorded here, as of a date: per build (a make PO line), units
 * built at the CM, shipped from it and received at HHL (good and rejected); and the parts at the CM then. Recorded by
 * production_run.record_opening through the ledger's own rules: output reports dated `as_of` (consumption follows the
 * BOM snapshot), an opening count at the CM site for the parts there, and the received units on the PO lines (no
 * receipt, so nothing is posted to stock or Shopify again).
 */
export const openingInput = z.object({
  as_of: z.iso.date("Use a date like 2026-10-01"),
  lines: z
    .array(
      z.object({
        make_order_id: z.uuid(),
        built: z.number().int("Whole units").min(0).max(10_000_000),
        shipped: z.number().int("Whole units").min(0).max(10_000_000).default(0),
        received: z.number().int("Whole units").min(0).max(10_000_000).default(0),
        rejected: z.number().int("Whole units").min(0).max(10_000_000).default(0),
      }),
    )
    .max(200)
    .default([]),
  parts: z
    .array(z.object({ item_id: z.uuid(), qty: z.number().nonnegative().max(100_000_000) }))
    .max(2000)
    .default([]),
  notes: z.string().trim().max(5000).default(""),
});
export type OpeningInput = z.infer<typeof openingInput>;
export type Opening = OpeningInput & { recorded_at: string; recorded_by: string | null; count_id: string | null; report_ids: string[] };
export const openingOf = (run: { opening: unknown }): Opening | null => {
  const o = run.opening as Partial<Opening> | null;
  return o && typeof o === "object" && o.as_of ? (o as Opening) : null;
};

// Fields ------------------------------------------------------------------------------------------------------

const text = (max = 200) => z.string().trim().max(max);
const isoDate = z.iso.date("Use a date like 2026-12-31").nullable();
const currency = z.string().trim().toUpperCase().length(3, "Use a 3-letter currency code");
const usdRate = z.number().positive("The rate to USD is more than 0").max(1_000_000);
const amount = z.number().min(-100_000_000).max(100_000_000);

export const productionRunFields = z.object({
  name: text(100),
  cm_vendor_id: z.uuid().nullable(),
  target_ship: isoDate,
  target_arrive: isoDate,
  notes: text(20_000),
  // A past run (migration 057): when it ran, on paper
  started_on: isoDate.nullable(),
  finished_on: isoDate.nullable(),
  // Set aside --- who has a past run's set-aside units and why (migration 069)
  set_aside_note: text(500),
});

export const runLineFields = z.object({
  run_id: z.uuid(),
  item_id: z.uuid(),
  qty: z.number().min(0, "Units are 0 or more").max(10_000_000),
  bom_id: z.uuid().nullable(),
  position: z.number().int().min(0).max(10_000),
  // Set aside --- of a past run's units built, those that weren't HHL's to sell (migration 069)
  set_aside: z.number().min(0, "Set aside is 0 or more").max(10_000_000),
});

const paid = {
  paid_on: isoDate,
  paid_method: z.enum(["", ...PAID_METHODS]),
  paid_ref: text(200),
  // Which card --- the account that paid it and where (migration 073); a card must say both
  paid_account_id: z.uuid().nullable(),
  paid_via: z.enum(["", ...PAID_VIA]),
};

export const productionCostFields = z.object({
  run_id: z.uuid(),
  category: z.enum(COST_CATEGORIES),
  vendor_id: z.uuid().nullable(),
  description: text(500),
  amount,
  currency,
  usd_rate: usdRate,
  basis: z.enum(COST_BASES),
  invoice_number: text(100),
  invoice_date: isoDate,
  po_id: z.uuid().nullable(),
  ...paid,
  notes: text(5000),
  // Pre-sales --- shipping to backers: which pre-sale, which shipment, and what kind of cost
  presale_id: z.uuid().nullable(),
  presale_shipment_id: z.uuid().nullable(),
  shipping_kind: z.enum(["", "postage", "ddp", "packing", "freight", "other"]),
});

export const toolingFields = z.object({
  project_id: z.uuid(),
  kind: z.enum(TOOLING_KINDS),
  name: z.string().trim().min(1, "Name it").max(200),
  vendor_id: z.uuid().nullable(),
  amount,
  currency,
  usd_rate: usdRate,
  invoice_number: text(100),
  invoice_date: isoDate,
  paid_by_run_id: z.uuid().nullable(),
  spread_units: z.number().int("Whole units").min(0).max(100_000_000),
  applies_when: z.record(z.string().trim().min(1).max(100), z.array(z.string().trim().min(1).max(200)).min(1).max(200)),
  kept_vendor_id: z.uuid().nullable(),
  kept_note: text(500),
  ...paid,
  retired: z.boolean(),
  notes: text(5000),
});

export const eventFields = z.object({
  run_id: z.uuid(),
  kind: z.enum(MANUAL_EVENT_KINDS),
  title: text(300),
  body: text(20_000),
  /** When it happened: a past moment for something recorded later. */
  at: z.iso.datetime({ offset: true }),
});

export const attachmentFields = z.object({ kind: z.enum(ATTACHMENT_KINDS), note: text(2000), doc_date: isoDate });

export const freightRateFields = z.object({
  project_id: z.uuid(),
  vendor_id: z.uuid().nullable(),
  basis: z.enum(["per_unit", "pct_value"]),
  rate: z.number().min(0, "A rate is 0 or more").max(99_999),
  notes: text(1000),
});

export const dutyRateFields = z.object({
  layer: z.string().trim().min(1, "Name the layer (e.g. Section 301)").max(100),
  hts_prefix: z
    .string()
    .trim()
    .transform((s) => s.replace(/\D/g, ""))
    .pipe(z.string().max(10, "HTS codes have up to 10 digits")),
  country: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^([A-Z]{2})?$/, "Use a 2-letter country code, like CN"),
  rate_pct: z.number().min(0, "A rate is 0 or more").max(999),
  effective_from: isoDate,
  effective_to: isoDate,
  notes: text(1000),
});

// Display helpers -------------------------------------------------------------------------------------------------

/** "PR-003 · Holiday restock" */
export const runLabel = (r: { number: string; name: string }) => (r.name ? `${r.number} · ${r.name}` : r.number);
